import {BarcodeFormat} from '@capacitor-mlkit/barcode-scanning';
import {CodeScan, toCodeScan} from './code-scan';

export type QrVideoScanner = {start(): Promise<void>; pause(immediate: boolean): Promise<boolean>; destroy(): void};
export type WebCodeDetector = {detect(video: HTMLVideoElement): Promise<{rawValue: string; format: string}[]>};
export const webProductFormats: Record<string, BarcodeFormat> = {
  ean_8: BarcodeFormat.Ean8, ean_13: BarcodeFormat.Ean13, upc_a: BarcodeFormat.UpcA,
};

/** Both decoders inspect one video; only the QR scanner owns its camera stream. */
export class WebCodeScanSession {
  readonly ready: Promise<void>;
  private stopped = false;
  private scanner?: QrVideoScanner;
  private detector: WebCodeDetector | null = null;
  private timer?: ReturnType<typeof setTimeout>;

  constructor(private readonly video: HTMLVideoElement,
    createQr: (detected: (value: string) => void) => Promise<QrVideoScanner>,
    createDetector: () => Promise<WebCodeDetector | null>,
    private readonly detected: (scan: CodeScan | null) => void,
    private readonly barcodeUnavailable: () => void) {
    this.ready = this.start(createQr, createDetector);
  }

  async stop(): Promise<void> {
    this.stopped = true;
    clearTimeout(this.timer);
    if (this.scanner) await this.scanner.pause(true);
    await this.ready.catch(() => {});
    this.scanner?.destroy();
  }

  private async start(createQr: (detected: (value: string) => void) => Promise<QrVideoScanner>,
    createDetector: () => Promise<WebCodeDetector | null>): Promise<void> {
    this.scanner = await createQr(value => this.emit(toCodeScan(value, BarcodeFormat.QrCode)));
    if (this.stopped) { this.scanner.destroy(); return; }
    try {
      await this.scanner.start();
      if (this.stopped) { await this.scanner.pause(true); return; }
      this.detector = await createDetector();
      if (this.stopped) return;
      if (!this.detector) this.barcodeUnavailable();
      void this.scanFrame();
    } catch (error) {
      await this.scanner.pause(true);
      this.scanner.destroy();
      throw error;
    }
  }

  private async scanFrame(): Promise<void> {
    if (this.stopped || !this.detector) return;
    try {
      const codes = await this.detector.detect(this.video);
      const code = codes.find(value => webProductFormats[value.format]);
      if (code) this.emit(toCodeScan(code.rawValue, webProductFormats[code.format]));
    } catch {
      this.detector = null;
      if (!this.stopped) this.barcodeUnavailable();
    }
    if (!this.stopped && this.detector) this.timer = setTimeout(() => void this.scanFrame(), 150);
  }

  private emit(scan: CodeScan | null): void {
    if (!this.stopped) this.detected(scan);
  }
}
